import { useEffect, useState } from 'react'
import Modal from 'react-modal'
import { BaseModalProps } from '../../../types/modal'
import TextInput from '../../ui/TextInput'

interface CoordinatesModalProps extends BaseModalProps {
  onCenter: (lat: number, lng: number) => void
  initialLat?: number | null
  initialLng?: number | null
}

/**
 * Modal to enter latitude/longitude and center the map on them. The "Center map"
 * button is the action; centering animates (zoom out → pan → zoom in) on the page.
 * A "lat, lng" pair pasted into the latitude field is auto-split for convenience.
 */
const CoordinatesModal = ({ isOpen, onClose, onCenter, initialLat, initialLng }: CoordinatesModalProps) => {
  const [lat, setLat] = useState('')
  const [lng, setLng] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setLat(initialLat != null ? initialLat.toFixed(6) : '')
    setLng(initialLng != null ? initialLng.toFixed(6) : '')
    setError(null)
  }, [isOpen, initialLat, initialLng])

  const resolveCoords = (): { lat: number; lng: number } | null => {
    let latStr = lat.trim()
    let lngStr = lng.trim()

    // Allow pasting "41.9, -87.64" (or space-separated) into the latitude field.
    if (!lngStr) {
      const pair = latStr.match(/^\s*(-?\d{1,3}(?:\.\d+)?)\s*[ ,]+\s*(-?\d{1,3}(?:\.\d+)?)\s*$/)
      if (pair) {
        latStr = pair[1]
        lngStr = pair[2]
      }
    }

    const latNum = parseFloat(latStr)
    const lngNum = parseFloat(lngStr)
    if (Number.isNaN(latNum) || Number.isNaN(lngNum)) {
      setError('Enter valid numbers for latitude and longitude.')
      return null
    }
    if (latNum < -90 || latNum > 90) {
      setError('Latitude must be between -90 and 90.')
      return null
    }
    if (lngNum < -180 || lngNum > 180) {
      setError('Longitude must be between -180 and 180.')
      return null
    }
    return { lat: latNum, lng: lngNum }
  }

  const handleCenter = () => {
    const coords = resolveCoords()
    if (!coords) return
    onCenter(coords.lat, coords.lng)
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="modal-popup confirm-popup role-modal"
      overlayClassName="modal-overlay"
      closeTimeoutMS={300}
    >
      <div className="modal-content-wrapper">
        <h2>Center on coordinates</h2>
        <form className="modal-body-content" onSubmit={e => e.preventDefault()}>
          <TextInput required type="text" label="Latitude" name="centerLat" value={lat} onChange={setLat} />
          <TextInput required type="text" label="Longitude" name="centerLng" value={lng} onChange={setLng} />
        </form>
        {error && (
          <div className="page-error" style={{ margin: 0 }}>
            {error}
          </div>
        )}
        <div className="modal-footer-content">
          <button type="button" className="styled-button" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="styled-button" onClick={handleCenter}>
            Center map
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default CoordinatesModal
